import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Users, Mail, Link2, Copy, Check, UserPlus, Shield, Eye, Pencil, Trash2, Crown, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleBadge = {
  owner: { label: "Owner", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  admin: { label: "Admin", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  editor: { label: "Editor", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  viewer: { label: "Viewer", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

export default function TeamAdminPage() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/teams/me`);
      setTeam(data.team);
      setMembers(data.members || []);
      setInvites((data.invites || []).filter(i => !i.used && i.type === "email"));
      setMyRole(data.my_role);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleInviteEmail = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteMsg(null);
    try {
      const { data } = await axios.post(`${API}/teams/invite/email`, { email: inviteEmail.trim(), role: inviteRole });
      setInviteMsg({ type: "success", text: data.message });
      setInviteEmail("");
      fetchTeam();
    } catch (err) {
      setInviteMsg({ type: "error", text: err.response?.data?.detail || "Failed to invite" });
    }
    setInviteLoading(false);
  };

  const handleGenerateLink = async () => {
    try {
      const { data } = await axios.post(`${API}/teams/invite/link`);
      setLinkToken(data.token);
      setLinkCopied(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate link");
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/join/${linkToken}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/teams/members/${userId}`, { role: newRole });
      fetchTeam();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await axios.put(`${API}/teams/members/${userId}`, { active: !currentActive });
      fetchTeam();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await axios.delete(`${API}/teams/members/${userId}`);
      fetchTeam();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove");
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse mb-6" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <Users size={48} className="mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">No team yet</h2>
        <p className="text-slate-500 text-sm">You're not part of a team. Team features will be available when you're added to one or create one from Settings.</p>
      </div>
    );
  }

  const isOwner = myRole === "owner";
  const canManage = myRole === "owner" || myRole === "admin";
  const activeMembers = members.filter(m => m.active);
  const inactiveMembers = members.filter(m => !m.active);

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{team.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{activeMembers.length} active member{activeMembers.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={fetchTeam} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Invite section */}
      {canManage && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><UserPlus size={16} /> Invite members</h2>

          {/* Email invite */}
          <form onSubmit={handleInviteEmail} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@agency.com"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
            />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              {isOwner && <option value="admin">Admin</option>}
            </select>
            <button type="submit" disabled={inviteLoading}
              className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors whitespace-nowrap">
              {inviteLoading ? "Sending..." : "Invite"}
            </button>
          </form>
          {inviteMsg && (
            <p className={`text-sm ${inviteMsg.type === "success" ? "text-emerald-600" : "text-red-500"}`}>{inviteMsg.text}</p>
          )}

          {/* Link invite */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2"><Link2 size={14} /> Invite link</p>
                <p className="text-xs text-slate-400">One-time use, expires in 24 hours</p>
              </div>
              <button onClick={handleGenerateLink}
                className="px-3 py-1.5 text-xs font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors">
                Generate link
              </button>
            </div>
            {linkToken && (
              <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                <code className="text-xs text-slate-600 flex-1 truncate">{window.location.origin}/join/{linkToken}</code>
                <button onClick={copyLink} className="p-1.5 rounded text-slate-400 hover:text-slate-600 transition-colors">
                  {linkCopied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                </button>
              </div>
            )}
          </div>

          {/* Pending invites */}
          {invites.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Pending invitations</p>
              {invites.map(inv => (
                <div key={inv.invite_id} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-600">{inv.email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${roleBadge[inv.role]?.cls}`}>{roleBadge[inv.role]?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Members list */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users size={16} /> Active members</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {activeMembers.map(m => (
            <div key={m.user_id} className="px-6 py-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                {m.picture ? <img src={m.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> :
                  <span className="text-xs font-bold text-orange-600">{m.name?.[0] || "?"}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{m.name || "Unnamed"} {m.user_id === user?.user_id && <span className="text-slate-400">(you)</span>}</p>
                <p className="text-xs text-slate-400 truncate">{m.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {m.role === "owner" ? (
                  <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><Crown size={10} /> Owner</span>
                ) : canManage && m.user_id !== user?.user_id ? (
                  <select value={m.role} onChange={e => handleUpdateRole(m.user_id, e.target.value)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 bg-white">
                    {isOwner && <option value="admin">Admin</option>}
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                ) : (
                  <span className={`text-xs px-2 py-1 rounded-full border ${roleBadge[m.role]?.cls}`}>{roleBadge[m.role]?.label}</span>
                )}
                {canManage && m.role !== "owner" && m.user_id !== user?.user_id && (
                  <>
                    <button onClick={() => handleToggleActive(m.user_id, m.active)} title="Deactivate"
                      className="p-1.5 rounded text-slate-300 hover:text-amber-500 transition-colors">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => handleRemove(m.user_id, m.name)} title="Remove"
                      className="p-1.5 rounded text-slate-300 hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inactive members */}
      {inactiveMembers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-500">Deactivated members</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {inactiveMembers.map(m => (
              <div key={m.user_id} className="px-6 py-4 flex items-center gap-4 opacity-50">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-slate-400">{m.name?.[0] || "?"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-600 truncate">{m.name || "Unnamed"}</p>
                  <p className="text-xs text-slate-400 truncate">{m.email}</p>
                </div>
                {canManage && (
                  <button onClick={() => handleToggleActive(m.user_id, m.active)}
                    className="text-xs px-3 py-1 rounded border border-slate-200 text-slate-500 hover:text-emerald-600 hover:border-emerald-300 transition-colors">
                    Reactivate
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
